import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map, retry, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { GameStateResponse, InventoryItem, InventoryResponse, ItemTypeConfig, ItemUse, ShopItem } from '../dto';
import { AbstractService } from './abstractservice';
import { ApplicationService } from './application.service';
import { JsonEnrichmentService } from './jsonmapper.service';
import { PetService } from './pet.service';

@Injectable({
    providedIn: 'root'
})
export class InventoryService extends AbstractService {

    private readonly activeUserInventoryState: WritableSignal<InventoryItem[]> = signal<InventoryItem[]>([]);
    public readonly activeUserInventorySignal: Signal<InventoryItem[]> = this.activeUserInventoryState.asReadonly();
    private inventoryInitialized = false;

    constructor(private http: HttpClient, private petService: PetService, private jsonMapper: JsonEnrichmentService, private coreAppService: ApplicationService) {
        super(coreAppService.authService, coreAppService.featureFlagService);
    }

    initializeInventory(): void {
        if (!this.inventoryInitialized) {
            this.getInventory().subscribe(inv => {
                this.inventoryInitialized = true;
                this.activeUserInventoryState.set(inv);
            });
        }
    }


    public getInventory(): Observable<InventoryItem[]> {
        return this.http.get<InventoryResponse>(environment.apiUrl + '/inventory/', this.httpOptionsAuthJson())
            .pipe(
                map(invResponse => {
                    invResponse = this.jsonMapper.map(invResponse, InventoryResponse);
                    return invResponse.Items;
                }),
                retry(2),
                catchError(this.handleError)
            );
    }

    public setInventory(items: InventoryItem[]): void {
        this.activeUserInventoryState.set(items);
    }

    public insertItem(item: InventoryItem): void {
        if (!item) { return; }

        this.activeUserInventoryState.set([item, ...this.activeUserInventoryState()]);
    }

    public insertItems(...items: InventoryItem[]): void {
        if (!items) { return; }

        this.activeUserInventoryState.set([...items, ...this.activeUserInventoryState()]);
    }

    public removeItems(...items: InventoryItem[]): void {
        this.removeItemsById(...items.map(i => i.Id));
    }

    public removeItemsById(...itemIds: number[]): void {
        if (!itemIds) { return; }

        this.activeUserInventoryState.set(this.activeUserInventoryState().filter(item => !itemIds.includes(item.Id)));
    }

    public getItemUses(itemType: ItemTypeConfig): Observable<ItemUse[]> {
        return this.http.get<ItemUse[]>(environment.apiUrl + `/inventory/uses/${itemType.Id}`, this.httpOptionsAuthJson())
            .pipe(
                retry(2),
                catchError(this.handleError)
            );
    }

    public useItem(item: InventoryItem, itemFunctionality: number, targetId: number): Observable<GameStateResponse> {
        return this.http.post<GameStateResponse>(environment.apiUrl + `/inventory/use/${item.Id}/${itemFunctionality}/${targetId ?? ''}`, null, this.httpOptionsAuthJson())
            .pipe(
                map(gameStateResponse => new GameStateResponse(gameStateResponse, this.jsonMapper.jsonEnricher())),
                tap({
                    next: result => GameStateResponse.distributeGameStateToServices(result, this, this.coreAppService.userService, this.petService)
                }),
                retry(2),
                catchError(this.handleError)
            );
    }

    public dropItem(item: InventoryItem): void {
        this.http.delete(environment.apiUrl + '/inventory/' + item.Id, this.httpOptionsAuthJson())
            .subscribe(() => this.removeItems(item));
    }

    public giftItems(items: InventoryItem[], recipient: string): Observable<void> {
        return this.http.patch<void>(environment.apiUrl + '/inventory/gift/' + recipient, items.map(i => i.Id), this.httpOptionsAuthJson())
            .pipe(tap(() => this.removeItems(...items)));
    }

    public moveItemsToShop(items: InventoryItem[], shop: number): Observable<void> {
        const shopItems = items.map(item => {
            const sitem = new ShopItem();
            sitem.Id = item.Id;
            sitem.ShopId = shop;
            sitem.Price = null;
            sitem.ShopDescription = null;
            return sitem;
        });

        return this.http.put<void>(environment.apiUrl + '/shop/stock/batch', shopItems, this.httpOptionsAuthJson())
            .pipe(tap(() => this.removeItems(...items)));
    }

    public getItemTypeConfigs(itemTypeIds: number[]) {
        return this.coreAppService.unifiedRepo.getOrRetrieve(true, true, ItemTypeConfig, ...itemTypeIds);
    }

}
